import React, {useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Accordion, Box, Button, Divider, Flex, Skeleton, Text} from '@chakra-ui/react'
import ProductSetCockpitTotal from './product-set-cockpit-total'
import CustomizablePsdpSetProductTile from './customizable-psdp-set-product-tile'
import AddToCartCTA from '../../../components/add-to-cart-cta'
import CockpitOrderSection from './cockpit-order-section'
import CustomizablePsdpCustomizationProducts from './customizable-psdp-customization-products'
import {FormattedMessage, useIntl} from 'react-intl'
import CompareCta from './compare-cta'
import ProductSpecialOffers from '../../../components/special-offer/product-special-offers'
import PayLater from '../../../components/pay-later'
import useProductPricing from '../../../hooks/use-product-pricing'
import QuantityPicker from '../../../components/quantity-picker'
import useMultiSite from '../../../hooks/use-multi-site'
import CustomizablePsdpSetProductTileWithThumbnails from './customizable-psdp-set-product-tile-with-thumbnails'
import useBreakpoints from '../../../hooks/use-breakpoints'

/**
 * A helper for determining if the accordion should be expanded
 * @param {undefined|'none'|'mobile'|'desktop'|'all'} expanded
 * @param {boolean} isMobile
 * @returns {boolean}
 */
const isExpanded = (expanded, isMobile) => {
    switch (expanded) {
        case 'all':
            return true
        case 'mobile':
            return isMobile
        case 'desktop':
            return !isMobile
        default:
            return false
    }
}

/**
 * Render the right column of the customizable product set detail page
 */
const CustomizablePsdpCockpitRightColumn = ({
    derivedProduct,
    isGetPropsLoading,
    isPromotionsLoading,
    isVariantLoading,
    product,
    productSpecialOffers,
    promotions,
    rightSidebarSizeProps,
    stickyProps,
    onAddToCartClick,
    onClickEngraving,
    onRecommendedProductClick,
    onHoverChange
}) => {
    const {formatMessage} = useIntl()
    const {site} = useMultiSite()
    const {displayPrice, currency} = useProductPricing(product) || {}
    const customizableSetConfig = useMemo(() => {
        try {
            return product.c_customizableSetConfig
                ? JSON.parse(product.c_customizableSetConfig)
                : {}
        } catch {
            return {}
        }
    }, [product?.c_customizableSetConfig])
    const isMobile = useBreakpoints({base: true, lg: false})
    // flag if user changed the accordion to stop updating the expanded state from the product
    const [userChangedAccordion, setUserChangedAccordion] = useState(false)
    // expanded indexes of the accordion
    const [accordionIndexes, setAccordionIndexes] = useState([
        ...(isExpanded(customizableSetConfig?.extensions?.expanded, isMobile) ? [0] : []),
        ...(isExpanded(customizableSetConfig?.accessories?.expanded, isMobile) ? [1] : [])
    ])
    // update expanded accordion indexes and mark that user have changed the accordion
    const onAccordionChange = useCallback((indexes) => {
        setAccordionIndexes(indexes)
        setUserChangedAccordion(true)
    }, [])
    // update expanded accordion indexes when product or mobile state changes
    useEffect(() => {
        if (!userChangedAccordion) {
            setAccordionIndexes([
                ...(isExpanded(customizableSetConfig?.extensions?.expanded, isMobile) ? [0] : []),
                ...(isExpanded(customizableSetConfig?.accessories?.expanded, isMobile) ? [1] : [])
            ])
        }
    }, [isMobile, userChangedAccordion, customizableSetConfig])
    // reset user changed accordion flag when product changes - i.e. different cpsdp
    useEffect(() => {
        setUserChangedAccordion(false)
    }, [product?.id])

    const {
        quantity,
        minOrderQuantity,
        maxOrderQuantity,
        stepQuantity,
        onChangeQuantity,
        isEngravable,
        isOrderable
    } = derivedProduct

    const screenReaderNotice = formatMessage({
        id: 'product_set_detail.cockpit.warning',
        defaultMessage:
            'Choosing an option will update the number of items and price total on the page and open a modal with additional color options.'
    })

    return (
        <Flex
            flexDirection="column"
            gap={{base: 0, lg: 6}}
            data-testid={'product-set-cockpit-right-column'}
            scrollMarginTop={24}
            {...stickyProps}
            {...rightSidebarSizeProps}
        >
            <Flex
                flexDirection="column"
                gap={4}
                paddingBlock={{base: 4, lg: 0}}
                paddingInline={{base: 4, lg: 0}}
                sx={{
                    '.swatch-group-list': {
                        marginInline: {base: -4, lg: 0},
                        paddingInline: {base: 4, lg: 0}
                    }
                }}
            >
                {/* Total price */}
                <Box
                    display={{base: 'none', lg: 'block'}}
                    paddingBottom={2.5}
                    borderBottom="1px"
                    borderColor="stokkeGray.dark"
                >
                    <ProductSetCockpitTotal
                        product={product}
                        isLoading={isGetPropsLoading || isPromotionsLoading}
                    />
                </Box>

                <Text className="sr-only">{screenReaderNotice}</Text>

                {/* List the setProducts - actually only their material and color variant selectors */}
                {customizableSetConfig?.colorDisplay === 'thumbnails' ? (
                    <Flex flexDirection="column" gap={4}>
                        {product.setProducts.map((setProduct, index) => (
                            <>
                                {index > 0 && <Divider borderColor="stokkeGray.bright" />}
                                <CustomizablePsdpSetProductTileWithThumbnails
                                    key={setProduct.setProductId}
                                    product={setProduct}
                                />
                            </>
                        ))}
                    </Flex>
                ) : (
                    product.setProducts.map((setProduct) => (
                        <CustomizablePsdpSetProductTile
                            key={setProduct.setProductId}
                            product={setProduct}
                            onHoverChange={onHoverChange}
                        />
                    ))
                )}
            </Flex>
            {/* The two accordions with extensions and accessories products */}
            {product.setExtensions?.length || product.setAccessories?.length ? (
                <Accordion
                    allowMultiple
                    display="flex"
                    flexDirection="column"
                    gap={6}
                    paddingInline={{base: 4, lg: 0}}
                    paddingBlockStart={{base: 8, lg: 6}}
                    paddingBlockEnd={4}
                    variant="unstyled"
                    index={accordionIndexes}
                    onChange={onAccordionChange}
                >
                    <Divider borderColor="stokkeGray.dark" />
                    {product.setExtensions?.length > 0 && (
                        <CustomizablePsdpCustomizationProducts
                            labelPrefix={
                                customizableSetConfig?.extensions?.labelPrefix
                                    ? customizableSetConfig.extensions.labelPrefix
                                    : '01'
                            }
                            label={
                                customizableSetConfig?.extensions?.label
                                    ? customizableSetConfig.extensions.label
                                    : formatMessage({
                                          id: 'cpsdp.extension_products.label',
                                          defaultMessage: 'Choose more options'
                                      })
                            }
                            products={product.setExtensions}
                            tileCheckboxEvent="choose_your_individual_set"
                            screenReaderNotice={screenReaderNotice}
                        />
                    )}
                    {product.setExtensions?.length > 0 && product.setAccessories?.length > 0 && (
                        <Divider borderColor="stokkeGray.dark" />
                    )}
                    {product.setAccessories?.length > 0 && (
                        <CustomizablePsdpCustomizationProducts
                            labelPrefix={
                                customizableSetConfig?.accessories?.labelPrefix
                                    ? customizableSetConfig.accessories.labelPrefix
                                    : '02'
                            }
                            label={
                                customizableSetConfig?.accessories?.label
                                    ? customizableSetConfig.accessories.label
                                    : formatMessage({
                                          id: 'cpsdp.accessories_products.label',
                                          defaultMessage: 'Choose your accessories'
                                      })
                            }
                            products={product.setAccessories}
                            tileCheckboxEvent="choose_your_accessories"
                        />
                    )}
                    <Divider borderColor="stokkeGray.dark" />
                </Accordion>
            ) : null}

            <Flex flexDirection="column" gap={4} paddingInline={{base: 4, lg: 0}}>
                {/* engraving */}
                {!site?.isContentOnlySite && isEngravable && (
                    <Button variant="outline" size="lg" onClick={onClickEngraving}>
                        <FormattedMessage
                            id="product_set_detail.cockpit.add_all_to_basket_incl_engraving"
                            defaultMessage="Add all to basket incl. engraving"
                        />
                    </Button>
                )}

                {/* Special offer on mobile */}
                <Skeleton isLoaded={!isPromotionsLoading && !isVariantLoading}>
                    <Box display={{base: 'block', lg: 'none'}}>
                        <ProductSpecialOffers {...productSpecialOffers} />
                    </Box>
                </Skeleton>

                {/* Another total price */}
                <ProductSetCockpitTotal
                    isLoading={isGetPropsLoading || isPromotionsLoading}
                    product={product}
                />

                <PayLater
                    amount={displayPrice}
                    currency={currency}
                    paypalPlacement="product"
                    klarnaPlacement="credit-promotion-badge"
                />

                {/* Add to cart CTA */}
                <Flex flexDirection="column" gap={3}>
                    {isOrderable && !site?.isContentOnlySite && (
                        <QuantityPicker
                            containerProps={{
                                // matches the button style
                                maxWidth: {md: '25rem'}
                            }}
                            btnWidth={15}
                            height={14}
                            minQuantity={minOrderQuantity}
                            maxQuantity={maxOrderQuantity}
                            quantity={quantity}
                            quantityStep={stepQuantity}
                            onChange={onChangeQuantity}
                        />
                    )}

                    <AddToCartCTA
                        isLoading={isGetPropsLoading || isPromotionsLoading || isVariantLoading}
                        product={product}
                        quantity={derivedProduct.quantity}
                        onClick={onAddToCartClick}
                        onRecommendedProductClick={onRecommendedProductClick}
                    />
                </Flex>

                <CompareCta product={product} />

                {/* Rest of order section */}
                <CockpitOrderSection
                    derivedProduct={derivedProduct}
                    isGetPropsLoading={isGetPropsLoading}
                    isPromotionsLoading={isPromotionsLoading}
                    isProductASet
                    isVariantLoading={isVariantLoading}
                    product={product}
                    productSpecialOffers={productSpecialOffers}
                    promotions={promotions}
                    onAddToCartClick={onAddToCartClick}
                />
            </Flex>
        </Flex>
    )
}

CustomizablePsdpCockpitRightColumn.propTypes = {
    derivedProduct: PropTypes.object,
    isGetPropsLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    product: PropTypes.object,
    productSpecialOffers: PropTypes.object,
    promotions: PropTypes.array,
    rightSidebarSizeProps: PropTypes.object,
    stickyProps: PropTypes.object,
    onAddToCartClick: PropTypes.func,
    onClickEngraving: PropTypes.func,
    onRecommendedProductClick: PropTypes.func,
    onHoverChange: PropTypes.func
}

export default CustomizablePsdpCockpitRightColumn
